import { useMemo } from 'react';
import { parseColorRule, type AnalysisColorRule, type ColorRule } from '@citizen-walk/shared-types';
import { useDistrict } from '../district';

/**
 * Sprint BG-DataSettings 6+7 (GAP-A4 / U-H8 / U-H9 / L17) — 地区 ColorRule 解決 hook。
 *
 * 解決順 (旧 v1 と同じ priority):
 *   1. `District.mapDisplay.colorRule` が `kind: 'value'|'quadrant'` 形式 → そのまま採用
 *   2. それが無いなら null (caller 側は markerStyle / MapLegend で mode-default fallback)
 *
 * M5 displayRules の旧 `AnalysisColorRule` (colorAxis 形式) は本 hook では採用しない:
 *   - colorAxis 系は M5 displayRules.map.colorRule → 旧 DistrictDisplayConfigPanel 経路
 *   - kind 系は District.mapDisplay → 本 hook の責務
 *   両者の同時設定が衝突した場合は admin 側で「両方は設定しない」運用を推奨 (旧 v1 と同 pattern)。
 *
 * 戻り値は `ColorRule | null`。`null` の場合、MapView 側で
 * `defaultMarkerColorForMode(mode)` の mode 既定色を marker に適用する。
 */
export function useColorRule(): ColorRule | null {
  const district = useDistrict();
  return useMemo(() => {
    const raw = district?.mapDisplay?.colorRule;
    if (!raw) return null;
    // `AnalysisColorRule` (colorAxis 持ち) は本 hook の責務外。kind 持ちのみ採用。
    if ('kind' in (raw as object)) {
      const parsed = parseColorRule(raw as unknown);
      return parsed ?? null;
    }
    // colorAxis 形式 (AnalysisColorRule) の場合は null を返す (旧 M5 displayRules ルート専用)
    void (raw as AnalysisColorRule);
    return null;
  }, [district?.mapDisplay?.colorRule]);
}
